import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { DesignToken } from '~/client/components/base/core';
import { spc } from '~/client/components/base/spacer';

interface GridItemProps {
  rowLine?: number | string;
  colLine?: number | string;
}

/**
 * GridItem wrapper to assign grid item locations
 * within the grid
 */
const GridItem = muiStyled('div')<GridItemProps>(({
  theme,
  rowLine,
  colLine,
}) => {
  return css`
    grid-row: ${rowLine};
    grid-column: ${colLine};
  `;
});

interface GridSeparatorProps {
  row: number;
}

/**
 * GridSeparator for rendering the separator line
 * between each row
 */
const GridSeparator = muiStyled('div')<GridSeparatorProps>(({ theme, row }) => {
  return css`
    grid-column: 1 / -1;
    border-bottom: 1px solid ${DesignToken.borderSubtle};
  `;
});

export interface TableProps extends React.PropsWithChildren {
  className?: string;
  /**
   * Number of columns to render (excluding the action column)
   * Default is 1
   */
  columns?: number;
  /**
   * All non action columns should have same width
   * Default is false, meaning column width depends on content
   */
  sameWidth?: boolean;
  /**
   * Render divider between rows
   */
  divider?: boolean;
  /**
   * Hide the action column
   * (shown as last column if false)
   */
  hideAction?: boolean;
}

const TableImpl: React.FC<TableProps> = ({ className, children, ...props }) => {
  // Unwrap React Fragments
  if (React.isValidElement(children) && children.type === React.Fragment) {
    return (
      <TableImpl
        className={className}
        {...props}
        children={children.props.children}
      />
    );
  }

  return (
    <div className={className}>
      {React.Children.map(children, (row, rowIdx) => {
        /**
         * Wrap all children of tableRow into a <GridItem/> component
         * All children that is not <TableAction/> will fall into
         * the row/column in natural order
         *
         * <TableAction/> will always be placed at the last column of the
         * grid
         */
        if (React.isValidElement(row)) {
          const lastRow = rowIdx + 1 === React.Children.count(children);
          let firstCol = true;
          const rowElements = React.Children.map(row.props?.children, (col) => {
            if (React.isValidElement(col)) {
              // @ts-expect-error
              const { displayName } = col.type;
              const isAction = displayName === 'TableAction';
              let colLine: string | number = isAction ? 'action' : 'auto';
              if (!isAction && firstCol) {
                colLine = 1;
                firstCol = false;
              }
              return (
                <GridItem colLine={colLine} {...row.props}>
                  {col}
                </GridItem>
              );
            }
          });
          if (props.divider && !lastRow) {
            rowElements?.push(<GridSeparator row={rowIdx} />);
          }
          return rowElements;
        }
      })}
    </div>
  );
};

/**
 * Render a table with arbitrary number of columns.
 *
 * - can render special child elements (i.e. `<TableAction/>`)
 *   in a specific location within the row.
 */
export const Table = muiStyled(TableImpl)(
  ({ theme, columns = 1, sameWidth, hideAction }) => css`
    display: grid;
    grid-template-columns:
      repeat(${columns}, ${sameWidth ? '1fr' : 'auto'})
      ${hideAction ? '' : '[action] auto'};
    /* Align items vertically by default */
    align-items: center;
    gap: ${theme.spacing(spc.between_vertical_s)}${theme.spacing(
        spc.between_horizontal_s
      )};
  `
);
